import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Repeat2, Wand2, Scissors, X, ChevronDown } from "lucide-react";
import { padCh } from "./dawHelpers";

/**
 * Sync & Quantize control panel.
 *
 * Sits between the Timeline ruler and the Timeline Arrangement so producers can
 * lock every loaded clip to the session tempo in one click — perfect when
 * loops were imported at different BPMs (e.g. a 90 BPM drum loop vs 120 BPM bass).
 *
 * - "Auto-Sync All" → engine.autoSyncChannels({ targetBars: "auto" })
 *   Every loaded channel flips into internal-loop mode and the global loop
 *   region becomes the LONGEST clip rounded to the nearest bar at session tempo.
 *
 * - "Quantize Selected · N bars" → engine.quantizeChannel(selectedChannel, N)
 *   Adjusts the selected channel's `playbackRate` so the buffer fits exactly
 *   N bars at the current tempo. NOTE: this changes pitch with speed (no
 *   phase-vocoder time-stretch yet). Users have been warned.
 *
 * - "Clear Sync" → engine.clearAutoSync()
 *   Restores every channel to playbackRate=1 + internalLoop=false.
 */
const BAR_OPTIONS = [1, 2, 4, 8, 16];

export default function SyncQuantizePanel({
  engine,
  selectedChannel,
  loadedCount,
  tempo,
  onSynced,
  onQuantized,
  onCleared,
}) {
  const [open, setOpen] = useState(false);
  const [bars, setBars] = useState(4);
  const [synced, setSynced] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleAutoSync = async () => {
    if (loadedCount === 0) {
      onSynced?.({ ok: false, reason: "no-audio" });
      return;
    }
    setBusy(true);
    try {
      const loopSec = engine.autoSyncChannels({ targetBars: "auto" });
      setSynced(true);
      onSynced?.({ ok: true, loopSeconds: loopSec });
    } finally {
      setBusy(false);
    }
  };

  const handleQuantize = () => {
    const ch = engine.channels?.[selectedChannel];
    if (!ch?.buffer) {
      onQuantized?.({ ok: false, reason: "no-buffer" });
      return;
    }
    const res = engine.quantizeChannel(selectedChannel, bars);
    if (res) {
      setSynced(true);
      onQuantized?.({ ok: true, ...res, channel: selectedChannel, bars });
    }
  };

  const handleClear = () => {
    engine.clearAutoSync();
    setSynced(false);
    onCleared?.();
  };

  return (
    <div
      data-testid="sync-quantize-panel"
      className="mb-3 rounded-2xl border border-white/10 bg-black/30 px-3 py-2"
    >
      <button
        type="button"
        data-testid="sync-quantize-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Repeat2 className={`h-4 w-4 ${synced ? "text-emerald-300" : "text-fuchsia-300"}`} />
          <span className="font-display text-sm font-semibold tracking-tight">
            Sync · Quantize
          </span>
          {synced && (
            <span
              data-testid="sync-active-badge"
              className="rounded-full bg-emerald-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-emerald-200 ring-1 ring-emerald-400/30"
            >
              Active
            </span>
          )}
          <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">
            · {tempo} BPM
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-white/50 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
          key="daw-syncquantizepanel-overlay"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="sync-auto-btn"
                onClick={handleAutoSync}
                disabled={busy || loadedCount === 0}
                title="Loop every loaded clip to fit the longest one rounded to the nearest bar at session tempo."
                className="flex items-center gap-1.5 rounded-xl bg-fuchsia-500/25 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-fuchsia-100 ring-1 ring-fuchsia-400/40 transition hover:bg-fuchsia-500/40 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Wand2 className="h-3 w-3" />
                Auto-Sync All
              </button>

              <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1.5">
                <span className="font-mono text-[10px] uppercase tracking-wider text-white/50">Bars</span>
                <select
                  data-testid="sync-bars-select"
                  value={bars}
                  onChange={(e) => setBars(Number(e.target.value))}
                  className="cursor-pointer rounded-md bg-black/40 px-2 py-0.5 font-mono text-[11px] text-white ring-1 ring-white/10 focus:outline-none focus:ring-fuchsia-400/40"
                >
                  {BAR_OPTIONS.map((n) => (
                    <option key={`bars-${n}`} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                data-testid="sync-quantize-channel-btn"
                onClick={handleQuantize}
                disabled={loadedCount === 0}
                title={`Stretch CH ${padCh(selectedChannel)} so it fits exactly ${bars} bar${bars === 1 ? "" : "s"} at ${tempo} BPM. (changes pitch with speed)`}
                className="flex items-center gap-1.5 rounded-xl bg-cyan-500/20 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-cyan-100 ring-1 ring-cyan-400/30 transition hover:bg-cyan-500/35 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Scissors className="h-3 w-3" />
                Quantize CH {padCh(selectedChannel)} · {bars}{bars === 1 ? " bar" : " bars"}
              </button>

              <button
                type="button"
                data-testid="sync-clear-btn"
                onClick={handleClear}
                disabled={!synced && loadedCount === 0}
                title="Restore every channel to its natural rate."
                className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-white/70 transition hover:bg-white/[0.1] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <X className="h-3 w-3" />
                Clear Sync
              </button>
            </div>

            <p className="mt-2 font-mono text-[9px] uppercase tracking-wider text-white/35">
              Auto-Sync locks every loop to the longest clip · Quantize stretches the selected channel to fit N bars at {tempo} BPM
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
